import {useState} from 'react';

import {SimpleGrid} from '@mantine/core';

import {useRegistrations} from '../hooks/useRegistrations.ts';
import {useRegistrationsTableColumns} from '../hooks/useRegistrationsTableColumns.tsx';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
    useTableSort,
} from '@flexinet/ui-components';

import {Registration} from '../types';
import {useAuth} from "../../../hooks/useAuth.ts";
import {useAllowedActions} from '../../../hooks/useAllowedActions.ts';
import {FormDrawer} from '../drawers/FormDrawer';
import {update} from '../services/RegistrationsService';
import {stripReadonlyFields} from '../../../utils/stripReadonlyFields';
import {useQueryClient} from '@tanstack/react-query';
import {I18n} from '@aws-amplify/core';

export const RecordsTable = ({
                                 selectedRecords,
                                 setSelectedRecords,
                             }: {
    selectedRecords: Registration[],
    setSelectedRecords: (records: Registration[]) => void,
}) => {

    const {allowedActions} = useAllowedActions();
    const queryClient = useQueryClient();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Registration | null>(null);

    const {
        user,
    } = useAuth();

    if (!user) {
        throw new Error('user is required parameter');
    }

    const cognitoUsername = user.username;

    const {
        visibleItems,
        loading,
        nextToken,
        isFetchingMore,
        fetchRecords,
        handleFilterByKeyword,
    } = useRegistrations();

    // Newest first. `created` is the only creation timestamp Registration has; it is
    // nullable in the schema, so rows without it sort last in both directions.
    // Under token pagination this sorts only the pages fetched so far, not the
    // whole dataset: a property of the model, not a bug.
    const {sortStatus, setSortStatus, sorted} = useTableSort<Registration>(visibleItems, {
        columnAccessor: 'created',
        direction: 'desc',
    });

    const handleRecordClick = (record: Registration) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const columns = useRegistrationsTableColumns(handleRecordClick);

    const handleDrawerSubmit = async (values: Registration) => {
        if (selectedRecord?.id) {
            await update(selectedRecord.id, stripReadonlyFields(values) as Registration);
            queryClient.invalidateQueries({queryKey: ['registrations']});
        }
    };

    // Only the creator or manager of a registration may select it for deletion.
    const selectionProps = allowedActions.includes('DeleteRegistration') ? {
        selectedRecords,
        onSelectedRecordsChange: setSelectedRecords,
        isRecordSelectable: (item: Registration) =>
            item.createdByUsername === cognitoUsername || item.managedByUsername === cognitoUsername,
    } : {};

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable<Registration>
                verticalSpacing='sm'
                striped
                highlightOnHover
                minHeight={150}
                records={sorted}
                columns={columns}
                fetching={loading}
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                noRecordsText={I18n.get('No records found')}
                {...selectionProps}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                nextToken={nextToken}
                handleLoadMore={fetchRecords}
                isFetchingMore={isFetchingMore}
                noun='registration'
            />

            <FormDrawer
                record={selectedRecord || {} as Registration}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};
